import { sample } from 'effector';
import { t } from 'i18n';
import $$api from 'models/api';
import $$i18n from 'models/i18n';
import { $isSummaryPage, $queryParams } from 'models/location';
import { showModal } from 'models/modals';
import $$summaryPage from 'models/pages/summary';
import navigate from 'models/router-gate';
import { $global } from 'models/store-global';
import { z } from 'zod';

import $$prePaymentPage from '../pre-payment';

import DashboardService from 'services/DashboardService';
import { createTunnel } from 'utils/factories/create-tunnel';

import { RegisteredModals } from 'types/Modals';

const updateAllFx = $$summaryPage.effects.updateAllFx.use(async (qInstanceId) => {
    await $$api.documents.generateFx({ questionnaireInstanceId: qInstanceId });
});

const updateChangedFx = $$summaryPage.effects.updateChangedFx.use(
    async ({ questionnaireInstanceId, changedDocuments }) => {
        await $$api.documents.generateFx({ questionnaireInstanceId, changedDocuments });
    },
);

const revertFx = $$summaryPage.effects.revertFx.use(async (qInstanceId) => {
    await DashboardService.cancelEstatePlan(qInstanceId);
    navigate('/dashboard');
});

// when isSummaryPage is opened, then get qInstanceId from query params, and set it to $qInstanceId
sample({
    source: $queryParams.map(({ qInstanceId }) => +qInstanceId || null),
    filter: $isSummaryPage,
    target: $$summaryPage.stores.$qInstanceId,
});

// when qInstanceId changed, if signed, then get changed docs
sample({
    source: $$summaryPage.stores.$qInstanceId,
    clock: $$summaryPage.stores.$answers.map((answers) => answers && answers?.hidden?.finalization?.signed),
    filter: (qInstanceId, isSigned): qInstanceId is number => isSigned === true && qInstanceId !== null,
    target: $$api.qInstances.getChangesFx,
});

// when getChanges loaded, then set changed docs
sample({
    source: $$api.qInstances.getChangesFx.doneData,
    target: $$summaryPage.stores.$changedDocs,
});

// when qInstanceId changed, then get answers
sample({
    source: {
        qInstanceId: $$summaryPage.stores.$qInstanceId,
        answers: createTunnel($global, (store) => store?.answers),
    },
    fn: ({ qInstanceId, answers }) => (qInstanceId && answers?.[qInstanceId]) ?? null,
    target: $$summaryPage.stores.$answers,
});

// when update all button clicked, if qInstanceId is not null, then update all
sample({
    source: $$summaryPage.stores.$qInstanceId,
    clock: $$summaryPage.events.updateAllButtonClicked,
    filter: (qInstanceId): qInstanceId is number => qInstanceId !== null,
    target: updateAllFx,
});

// when update changed button clicked, if qInstanceId is not null, then update changed
sample({
    source: {
        questionnaireInstanceId: $$summaryPage.stores.$qInstanceId,
        changedDocuments: $$summaryPage.stores.$changedDocs,
    },
    clock: $$summaryPage.events.updateChangedButtonClicked,
    filter: ({ questionnaireInstanceId }) => questionnaireInstanceId !== null,
    fn: ({ questionnaireInstanceId, changedDocuments }) => {
        return { questionnaireInstanceId: z.number().parse(questionnaireInstanceId), changedDocuments };
    },
    target: updateChangedFx,
});

// when revert button clicked, if qInstanceId is not null, then revert
sample({
    source: $$summaryPage.stores.$qInstanceId,
    clock: $$summaryPage.events.revertButtonClicked,
    filter: (qInstanceId): qInstanceId is number => qInstanceId !== null,
    target: revertFx,
});

// when generate button clicked and mustGoToModality is true, then trigger openMissingCharityModality
sample({
    source: $$prePaymentPage.$mustGoToModality,
    clock: $$summaryPage.events.generateButtonClicked,
    filter: (mustGoToModality) => mustGoToModality,
    target: $$prePaymentPage.openMissingCharityModality,
});

// when generate button clicked, if signed and qInstanceId is not null, then show modal
sample({
    source: {
        qInstanceId: $$summaryPage.stores.$qInstanceId,
        isSigned: $$summaryPage.stores.$answers.map((answers) => answers && answers?.hidden?.finalization?.signed),
        confirmationModalEnabled: $$summaryPage.$confirmationModalEnabled,
        mustGoToModality: $$prePaymentPage.$mustGoToModality,
    },
    clock: $$summaryPage.events.generateButtonClicked,
    filter: ({ isSigned, qInstanceId, confirmationModalEnabled, mustGoToModality }) =>
        isSigned === true && qInstanceId !== null && confirmationModalEnabled && !mustGoToModality,
    fn: () => ({ modalName: 'CONFIRM_GENERATION_MODAL' as RegisteredModals }),
    target: showModal,
});

// when generate button clicked, if not signed or qInstanceId is null, then generate
sample({
    source: {
        qInstanceId: $$summaryPage.stores.$qInstanceId,
        isSigned: $$summaryPage.stores.$answers.map((answers) => answers && answers?.hidden?.finalization?.signed),
        confirmationModalEnabled: $$summaryPage.$confirmationModalEnabled,
        mustGoToModality: $$prePaymentPage.$mustGoToModality,
    },
    clock: $$summaryPage.events.generateButtonClicked,
    filter: ({ isSigned, qInstanceId, confirmationModalEnabled, mustGoToModality }) =>
        !(isSigned === true && qInstanceId !== null) && confirmationModalEnabled && !mustGoToModality,
    fn: ({ qInstanceId }) => ({ questionnaireInstanceId: z.number().parse(qInstanceId) }),
    target: $$api.documents.generateFx,
});

// Always generate without showing a modal.
sample({
    source: {
        qInstanceId: $$summaryPage.stores.$qInstanceId,
        confirmationModalEnabled: $$summaryPage.$confirmationModalEnabled,
        mustGoToModality: $$prePaymentPage.$mustGoToModality,
    },
    clock: $$summaryPage.events.generateButtonClicked,
    filter: ({ qInstanceId, confirmationModalEnabled, mustGoToModality }) =>
        qInstanceId !== null && !confirmationModalEnabled && !mustGoToModality,
    fn: ({ qInstanceId }) => ({ questionnaireInstanceId: z.number().parse(qInstanceId) }),
    target: $$api.documents.generateFx,
});

sample({
    source: $$api.documents.generateFx.pending,
    target: $$summaryPage.stores.$isGenerationRequestPending,
});

sample({
    clock: $$i18n.localesLoaded,
    fn: () => (t('features.confirmDocsGenerationModal') as unknown) === true,
    target: $$summaryPage.$confirmationModalEnabled,
});
